var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-field-selection-drawer.ts
import { isGQLScalarType } from "@adornis/baseql/baseqlTypes.js";
import { ChemistryLitElement } from "@adornis/chemistry/chemistry-lit-element.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-flex.js";
import "@adornis/chemistry/elements/components/x-hr.js";
import "@adornis/chemistry/elements/components/x-icon.js";
import "@adornis/chemistry/elements/components/x-tree-view.js";
import { xComponents } from "@adornis/chemistry/elements/x-components.js";
import "@adornis/dialog/x-drawer-panel.js";
import { TranslationController } from "@adornis/translation-core/client/translation-controller.js";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { BehaviorSubject, debounceTime, map } from "rxjs";
import { FieldConfiguration, makeUniqueIDForFieldConf } from "./field-configuration.js";
import {
  buildFullFieldManagementOptions
} from "./field-management-options.js";
import { translateFieldConfiguration, translateFieldName } from "./translate-field.js";
function searchFilter(searchString, contents) {
  return contents.some((c) => c?.toLowerCase().includes(searchString.toLowerCase()));
}
var XFieldSelectionDrawer = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.options = null;
    this._translation = new TranslationController(this);
    this.searchSubject = new BehaviorSubject("");
    this._search = new RXController(
      this,
      this.searchSubject.pipe(
        debounceTime(200),
        map((v) => v.toLowerCase())
      )
    );
    this._drawer = createRef();
  }
  hasColumn(id) {
    if (!this._fullOptions.selected)
      return false;
    return !!this._fullOptions.selected.find((col) => makeUniqueIDForFieldConf(col) === makeUniqueIDForFieldConf(id));
  }
  addColumnIfDoesntExist(id) {
    this._fullOptions.selected?.push(new FieldConfiguration({ ...id, columnWidth: "220px" }));
    this.notifyChange();
  }
  removeColumn(id) {
    this._fullOptions.selected?.splice(
      this._fullOptions.selected?.findIndex((col) => makeUniqueIDForFieldConf(col) === makeUniqueIDForFieldConf(id)),
      1
    );
    this.notifyChange();
  }
  notifyChange() {
    this.requestUpdate();
    requestAnimationFrame(() => this._fullOptions.onSelectedChanged?.(this._fullOptions.selected));
  }
  willUpdate(chp) {
    super.willUpdate(chp);
    if (!this._fullOptions && this.options)
      this._fullOptions = buildFullFieldManagementOptions(this.options);
  }
  async chooseFields(options) {
    const promise = new Promise((resolve) => this._resolveChooseFields = resolve);
    if (options)
      this.options = options;
    if (!this.options)
      throw new Error("dont support running without options");
    await this.updateComplete;
    this._drawer.value?.open();
    await promise;
    const newSelection = this._fullOptions.selected;
    this.options = null;
    this._fullOptions = null;
    return newSelection;
  }
  _close() {
    this._resolveChooseFields?.();
  }
  render() {
    const topbottom = {
      background: this.colors.white,
      boxShadow: `0 0 10px ${this.colors.neutral.shade(100)}`,
      zIndex: 1
    };
    return xComponents(html`
      <x-drawer-panel ${ref(this._drawer)} @close=${() => requestAnimationFrame(() => this._close())}>
        ${this.options && this._fullOptions ? html`
              <x-flex ${css({ background: this.colors.white, width: "500px", maxWidth: "90vw" })} h100>
                <x-flex ${css(topbottom)}>
                  <x-flex space="sm" horizontal crossaxis-center w100 padding="xs">
                    <x-icon> add_circle </x-icon>
                    <x-h4 flex>
                      ${this._fullOptions.title ?? this._translation.translate("fieldManagement", "addNewField")}
                    </x-h4>
                    <x-button
                      mode="icon"
                      icon="delete"
                      @click=${() => {
      this._fullOptions.selected.splice(0, this._fullOptions.selected.length);
      this.notifyChange();
    }}
                    >
                      <x-tooltip
                        .text=${xComponents(
      html`
                            <x-text> ${this._translation.translate("fieldManagement", "deleteSelection")} </x-text>
                          `
    )}
                      ></x-tooltip>
                    </x-button>
                  </x-flex>
                  <x-hr></x-hr>
                </x-flex>

                <x-flex flex ${css({ overflow: "auto" })}> ${this.content()} </x-flex>

                <x-flex ${css(topbottom)}>
                  <x-hr></x-hr>
                  <x-flex space="sm" horizontal end crossaxis-center padding="xs" w100>
                    <x-button mode="text" icon="check" @click=${() => this._drawer.value?.close()}>
                      ${this._translation.translate("fieldManagement", "close")}
                    </x-button>
                  </x-flex>
                </x-flex>
              </x-flex>
            ` : nothing}
      </x-drawer-panel>
    `);
  }
  content() {
    return html`
      <x-flex space="xs">
        ${this.searchBar()} ${this.extraFieldsTemplate()} ${this.existingFields()} ${this.addNewField()}
      </x-flex>
    `;
  }
  searchBar() {
    return html`
      <x-input
        autofocus
        ${css({ position: "sticky", top: "0", zIndex: "1" })}
        placeholder=${this._translation.translate("fieldManagement", "search")}
        clearable
        @value-changed=${(e) => this.searchSubject.next(e.detail.value ?? "")}
      ></x-input>
    `;
  }
  /** renders fields that are included by options.customColumns and do not have a corresponding field that they can be shown as */
  extraFieldsTemplate() {
    const encountered = /* @__PURE__ */ new Set();
    const extraFields = this._fullOptions.extraFields.filter((field) => {
      const ok = !!field.customID && !encountered.has(field.customID) && searchFilter(this._search.value ?? "", [field.customID, field.fieldName, translateFieldConfiguration(field)]);
      if (ok && !!field.customID) {
        encountered.add(field.customID);
      }
      return ok;
    });
    if (!extraFields.length)
      return nothing;
    return xComponents(html`
      ${this.renderHeading(this._translation.translate("fieldManagement", "extraFields"))}
      <x-tree-view
        nested
        .data=${extraFields.map(
      (fc) => ({
        title: translateFieldConfiguration(fc),
        extra: {
          isSelected: this.hasColumn({ fieldName: fc.fieldName, customID: fc.customID }),
          customID: fc.customID,
          completePath: fc.fieldName
        },
        renderItem: (node) => this.renderTreeNode(node)
      })
    )}
      ></x-tree-view>
    `);
  }
  existingFields() {
    if (!this._fullOptions.class)
      return nothing;
    const keysToNodes = (clazz, path = []) => {
      const fields = this._fullOptions.getAvailableFieldsWithSearch(clazz, path, this._search.value);
      return fields.map((field) => {
        const newPath = [...path, field.name];
        let title = translateFieldName(newPath);
        if (title.includes(">"))
          title = title.split(">").at(-1);
        return {
          title,
          extra: {
            isSelected: this.hasColumn({ fieldName: newPath.join(".") }),
            completePath: newPath.join("."),
            customID: void 0,
            field
          },
          children: () => {
            const isPrimitive = isGQLScalarType(field.type);
            return isPrimitive ? void 0 : keysToNodes(field.type(), newPath);
          },
          renderItem: (node) => {
            return this.renderTreeNode(node);
          }
        };
      });
    };
    return xComponents(html`
      ${this.renderHeading(this._translation.translate("fieldManagement", "fields"))}
      <x-tree-view nested allow-multiple-extended .data=${keysToNodes(this._fullOptions.class)}></x-tree-view>
    `);
  }
  addNewField() {
    return nothing;
    return this.renderHeading(this._translation.translate("fieldManagement", "cmsFields"));
  }
  renderTreeNode(node) {
    const newNode = { fieldName: node.extra?.completePath, customID: node.extra?.customID };
    const isSelectable = node.computedChildren.length ? !!newNode.customID || !!this._fullOptions.extraFields.find((c) => c.fieldName === newNode.fieldName) : true;
    return html`
      <x-flex w100 horizontal crossaxis-center space="xs" ${css({ userSelect: "none", minHeight: "1.2rem" })}>
        ${isSelectable ? html`
              <x-checkbox
                ${css({
      opacity: isSelectable ? node.extra?.isSelected ? 1 : 0.3 : "0",
      pointerEvents: isSelectable ? "unset" : "none"
    })}
                .value=${this.hasColumn(newNode)}
                @value-picked=${(e) => {
      if (!e.detail.value)
        this.removeColumn(newNode);
      else
        this.addColumnIfDoesntExist(newNode);
    }}
                @click=${(e) => e.stopPropagation()}
              ></x-checkbox>
            ` : nothing}

        <x-text ellipsis flex ${css({ width: "0" })} .value=${node.title}> </x-text>
      </x-flex>
    `;
  }
  renderHeading(title) {
    return xComponents(html`
      <x-flex
        ${css({
      position: "sticky",
      top: "38px",
      padding: this.spacing.sm,
      background: this.colors.light,
      zIndex: "1"
    })}
      >
        <x-h4> ${title} </x-h4>
      </x-flex>
    `);
  }
};
__decorateClass([
  property({ attribute: false })
], XFieldSelectionDrawer.prototype, "options", 2);
__decorateClass([
  state()
], XFieldSelectionDrawer.prototype, "_fullOptions", 2);
__decorateClass([
  state()
], XFieldSelectionDrawer.prototype, "_translation", 2);
__decorateClass([
  state()
], XFieldSelectionDrawer.prototype, "_search", 2);
XFieldSelectionDrawer = __decorateClass([
  customElement("x-field-selection-drawer")
], XFieldSelectionDrawer);
export {
  XFieldSelectionDrawer
};
